import { toSnakeCase } from '@/lib/utils/fieldTransformers';
import { toCamelCase } from '@/lib/utils/requestTransformers';
import { redirectToLoginOnTokenExpired } from '@/lib/utils/authRedirect';
import axios, { InternalAxiosRequestConfig } from 'axios';

// Get base URL based on environment:
// - Development (Docker or Local): Browser uses /api (proxied by Next.js rewrites), Server uses appropriate URL
// - Railway (production): Browser and Server both use NEXT_PUBLIC_API_URL (full HTTPS URL)
const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    // Browser environment
    const apiUrl = process.env.NEXT_PUBLIC_API_URL;

    // In development, Next.js rewrites will proxy /api to backend
    // So we use relative URL /api for development
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (isDevelopment) {
      // Development: use relative URL, Next.js rewrites will proxy to backend
      return '/api';
    }

    // Production (Railway): use full URL from NEXT_PUBLIC_API_URL
    return apiUrl || '/api';
  }

  // Server-side rendering
  // NGINX_URL is available in server-side (set in docker-compose.yml for Docker)
  if (process.env.NGINX_URL) {
    // Docker development: use nginx service name
    return `${process.env.NGINX_URL}/api`;
  }
  // Local development or Railway production: use NEXT_PUBLIC_API_URL or fallback
  return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/api';
};

const apiClient = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds default
  // Temporarily disable withCredentials to test if it's causing the issue
  // withCredentials: true, // Required when Access-Control-Allow-Credentials is true
});

// Extended timeout client for AI and long-running requests (5 minutes)
// Use this for operations that may take longer like AI processing, large content generation, etc.
export const apiClientExtended = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes for AI/long operations
});

// Request interceptor - Convert camelCase to snake_case before sending
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Transform request data from camelCase to snake_case (always transform so mixed keys work)
    if (
      config.data &&
      typeof config.data === 'object' &&
      !(config.data instanceof FormData) &&
      !Array.isArray(config.data)
    ) {
      config.data = toSnakeCase(config.data);
    }

    // Transform query parameters from camelCase to snake_case
    if (config.params && typeof config.params === 'object' && !Array.isArray(config.params)) {
      config.params = toSnakeCase(config.params);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Convert snake_case to camelCase after receiving
apiClient.interceptors.response.use(
  (response) => {
    // IMPORTANT: Skip transforming binary responses (e.g. file downloads)
    // Otherwise we may accidentally "camelCase" a Blob/ArrayBuffer and break it.
    const responseType = response?.config?.responseType;
    const data = response?.data;
    const isBinaryResponse =
      responseType === 'blob' ||
      responseType === 'arraybuffer' ||
      responseType === 'stream' ||
      (typeof Blob !== 'undefined' && data instanceof Blob) ||
      (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer);

    if (isBinaryResponse) {
      return response;
    }

    // Transform response data from snake_case to camelCase
    if (response.data && typeof response.data === 'object') {
      // Handle nested data structure: { data: {...}, pagination: {...} }
      // This is for responses like CategoryListResponse: { data: [...], pagination: {...} }
      if (response.data.data !== undefined) {
        // ✅ Defensive check: handle null/undefined data
        if (response.data.data === null || response.data.data === undefined) {
          // Ensure data is always an array, not null
          response.data.data = [];
        } else if (Array.isArray(response.data.data)) {
          // Transform array items
          response.data.data = response.data.data.map((item: any) => toCamelCase(item));
        } else if (typeof response.data.data === 'object' && response.data.data !== null) {
          // Transform single object
          response.data.data = toCamelCase(response.data.data);
        }
        // Transform pagination if it exists
        if (response.data.pagination && typeof response.data.pagination === 'object') {
          response.data.pagination = toCamelCase(response.data.pagination);
        }
      } else if (Array.isArray(response.data)) {
        // Direct array response
        // ✅ Defensive check: ensure it's an array before mapping (always safe, but check for safety)
        if (response.data && response.data.length >= 0) {
          response.data = response.data.map((item: any) => toCamelCase(item));
        }
      } else {
        // Single object response
        response.data = toCamelCase(response.data);
      }
    }
    return response;
  },
  async (error) => {
    // Skip logging 404 errors for expected cases:
    // 1. Contracts/project endpoint (expected when project has no contract)
    // 2. Suppliers/me endpoint (expected when user is registered but hasn't created supplier profile yet)
    const url = error?.config?.url || '';
    const isContractProject404 =
      error?.response?.status === 404 && url.includes('/v1/contracts/project/');
    const isSupplierMe404 = error?.response?.status === 404 && url.includes('/v1/suppliers/me');

    if (isContractProject404 || isSupplierMe404) {
      // Silently return the error - it will be handled by the respective hooks
      return Promise.reject(error);
    }

    // Log error details for debugging
    try {
      // During build/SSR, ECONNREFUSED is expected when backend isn't running (e.g. sitemap).
      // Use minimal logging to avoid noisy build output.
      const isServerSide = typeof window === 'undefined';
      const isConnectionRefused =
        (error.code === 'ECONNREFUSED' || error.message?.includes('ECONNREFUSED')) &&
        !error.response &&
        error.request;

      if (isServerSide && isConnectionRefused) {
        console.warn(
          `[API] Backend unavailable (ECONNREFUSED) for ${error.config?.url} — skipping. Run backend for full sitemap.`
        );
      } else {
        // First, log the raw error structure to understand what we're dealing with
        console.error('🔴 Raw Error Object:', {
          type: typeof error,
          constructor: error?.constructor?.name,
          keys: Object.keys(error || {}),
          hasResponse: 'response' in (error || {}),
          hasConfig: 'config' in (error || {}),
          hasRequest: 'request' in (error || {}),
          error: error,
        });

      if (error.response) {
        // Response error (4xx, 5xx)
        // Extract values directly to avoid any proxy/getter issues
        const responseStatus = error.response.status;
        const responseStatusText = error.response.statusText;
        const responseData = error.response.data;
        const configUrl = error.config?.url;
        const configMethod = error.config?.method;
        const configBaseURL = error.config?.baseURL;
        const requestResponseURL = error.request?.responseURL;
        const errorMessage = error.message;
        const errorName = error.name;
        const errorCode = (error as any).code;

        // Build errorInfo with explicit property assignments to ensure they're set
        const errorInfo: Record<string, any> = {};

        // Always set these properties explicitly with extracted values
        errorInfo.status = responseStatus ?? 'unknown';
        errorInfo.statusText = responseStatusText ?? 'unknown';
        errorInfo.url = configUrl || requestResponseURL || 'unknown';
        errorInfo.method = (configMethod?.toUpperCase() || 'UNKNOWN') as string;
        errorInfo.message = errorMessage || 'No error message';
        errorInfo.timestamp = new Date().toISOString();
        // Phase 5: include request_id for backend correlation when present
        const reqId =
          (responseData && typeof responseData === 'object' && (responseData.request_id ?? (responseData as any).requestId)) ||
          error.response?.headers?.['x-request-id'];
        if (reqId) errorInfo.request_id = reqId;

        // Always add data, even if it's null or empty, to help with debugging
        if (responseData !== undefined && responseData !== null) {
          errorInfo.data = responseData;
        } else if (responseData === null) {
          errorInfo.data = null;
        } else {
          errorInfo.data = 'No response data';
        }

        // Only add params if they exist
        if (error.config?.params !== undefined && error.config?.params !== null) {
          errorInfo.params = error.config.params;
        }

        // Add baseURL for context
        if (configBaseURL) {
          errorInfo.baseURL = configBaseURL;
        }

        // Log individual properties first to see what we have
        console.error('🔴 API Error - Individual Properties:', {
          'error.response.status': responseStatus,
          'error.response.statusText': responseStatusText,
          'error.response.data': responseData,
          'error.config.url': configUrl,
          'error.config.method': configMethod,
          'error.config.baseURL': configBaseURL,
          'error.message': errorMessage,
          'error.name': errorName,
          'error.code': errorCode,
        });

        // Log the structured error info
        console.error('API Error (Response):', errorInfo);

        // Also log as stringified to see actual values
        try {
          console.error('API Error (Response) - Stringified:', JSON.stringify(errorInfo, null, 2));
        } catch (e) {
          console.error('Could not stringify errorInfo:', e);
        }

        // Always log additional debugging info to help diagnose issues
        const errorDetails: Record<string, any> = {};
        errorDetails.hasResponse = !!error.response;
        errorDetails.hasConfig = !!error.config;
        errorDetails.hasRequest = !!error.request;
        errorDetails.responseStatus = responseStatus;
        errorDetails.responseStatusText = responseStatusText;
        errorDetails.responseData = responseData;
        errorDetails.responseDataType = typeof responseData;
        errorDetails.responseDataIsNull = responseData === null;
        errorDetails.responseDataIsUndefined = responseData === undefined;
        errorDetails.configUrl = configUrl;
        errorDetails.configMethod = configMethod;
        errorDetails.configBaseURL = configBaseURL;
        errorDetails.errorMessage = errorMessage;
        errorDetails.errorName = errorName;
        errorDetails.errorCode = errorCode;
        errorDetails.errorStack = error.stack?.split('\n').slice(0, 3).join('\n');

        console.error('Error Details:', errorDetails);

        // Also log as stringified
        try {
          console.error('Error Details - Stringified:', JSON.stringify(errorDetails, null, 2));
        } catch (e) {
          console.error('Could not stringify errorDetails:', e);
        }
      } else if (error.request) {
        // Request made but no response received (network error)
        const baseURL =
          error.config?.baseURL || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/api';
        const url = error.config?.url || '';
        const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;

        const errorInfo = {
          message: error.message || 'Network error occurred',
          code: error.code || 'UNKNOWN',
          url: url || 'Unknown URL',
          fullUrl: fullUrl,
          baseURL: baseURL,
          method: error.config?.method?.toUpperCase() || 'UNKNOWN',
          timeout: error.config?.timeout,
          requestMade: !!error.request,
          responseReceived: !!error.response,
        };

        console.group('🔴 API Network Error');
        console.error('Error Info:', errorInfo);
        console.error('Error Message:', error.message);
        console.error('Error Code:', error.code);

        // Provide helpful diagnostic messages
        if (error.code === 'ECONNREFUSED' || error.message?.includes('ECONNREFUSED')) {
          console.error('💡 Diagnostic: Server connection refused.');
          console.error(
            `   → Make sure backend server is running on ${baseURL.replace('/api', '')}`
          );
          console.error('   → Try accessing the server URL directly in your browser');
          console.error('   → Check backend server logs for startup errors');
        } else if (error.code === 'ETIMEDOUT' || error.code === 'ECONNABORTED') {
          console.error('💡 Diagnostic: Request timed out.');
          console.error('   → Server may be slow or overloaded');
          console.error('   → Check backend server logs');
          console.error(
            `   → Try increasing timeout (current: ${error.config?.timeout || 'default'}ms)`
          );
        } else if (error.message?.includes('Network Error') || error.message?.includes('network')) {
          console.error('💡 Diagnostic: Network request failed.');
          console.error('   → Check your internet connection');
          console.error(
            `   → Verify backend server is accessible at ${baseURL.replace('/api', '')}`
          );
          console.error('   → Check CORS settings if accessing from different origin');
          console.error('   → Try opening the server URL in a new browser tab');
        } else {
          console.error('💡 Diagnostic: Unknown network error.');
          console.error('   → Full error details below');
        }

        // Log raw error details
        console.error('Full Error Object:', {
          name: error.name,
          message: error.message,
          code: error.code,
          stack: error.stack?.split('\n').slice(0, 5).join('\n'), // First 5 lines of stack
          config: {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
            timeout: error.config?.timeout,
          },
        });
        console.groupEnd();
      } else {
        // Error in request setup
        const errorInfo: Record<string, any> = {
          message: error.message || 'Unknown error',
        };

        if (error.stack) {
          errorInfo.stack = error.stack;
        }

        // Add any other error properties that might be useful
        if (error.name) {
          errorInfo.name = error.name;
        }

        console.error('API Error (Setup):', errorInfo);
        // Also log the raw error for debugging
        console.error('Setup Error Details:', error);
      }
      }
    } catch (logError) {
      // Fallback: if error logging itself fails, log the raw error
      console.error('Failed to log error details. Raw error:', error);
      console.error('Logging error:', logError);

      // Last resort: try to stringify the error
      try {
        console.error(
          'Error stringified:',
          JSON.stringify(error, Object.getOwnPropertyNames(error))
        );
      } catch {
        console.error('Could not stringify error');
      }
    }

    // Handle 401 errors (token expired or invalid)
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const isAuthPage = currentPath.includes('/login') || currentPath.includes('/register');

        if (!isAuthPage) {
          localStorage.removeItem('token');
          // Redirect to login while preserving current page for return after login
          redirectToLoginOnTokenExpired();
        }
      }
    }
    // For network errors or 404/500 errors, let them pass through
    // so API functions can catch them and use mock data fallback
    return Promise.reject(error);
  }
);

// Apply the same interceptors to the extended timeout client
// Request interceptor for extended client
apiClientExtended.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Transform request data and params to snake_case (always, for consistency)
    if (
      config.data &&
      typeof config.data === 'object' &&
      !(config.data instanceof FormData) &&
      !Array.isArray(config.data)
    ) {
      config.data = toSnakeCase(config.data);
    }
    if (config.params && typeof config.params === 'object' && !Array.isArray(config.params)) {
      config.params = toSnakeCase(config.params);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for extended client - simplified for AI responses
apiClientExtended.interceptors.response.use(
  (response) => {
    // IMPORTANT: Skip transforming binary responses (e.g. file downloads)
    const responseType = response?.config?.responseType;
    const data = response?.data;
    const isBinaryResponse =
      responseType === 'blob' ||
      responseType === 'arraybuffer' ||
      responseType === 'stream' ||
      (typeof Blob !== 'undefined' && data instanceof Blob) ||
      (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer);

    if (isBinaryResponse) {
      return response;
    }

    // Transform response data from snake_case to camelCase
    if (response.data && typeof response.data === 'object') {
      if (response.data.data !== undefined) {
        if (response.data.data === null || response.data.data === undefined) {
          response.data.data = [];
        } else if (Array.isArray(response.data.data)) {
          response.data.data = response.data.data.map((item: any) => toCamelCase(item));
        } else if (typeof response.data.data === 'object' && response.data.data !== null) {
          response.data.data = toCamelCase(response.data.data);
        }
        if (response.data.pagination && typeof response.data.pagination === 'object') {
          response.data.pagination = toCamelCase(response.data.pagination);
        }
      } else if (Array.isArray(response.data)) {
        if (response.data && response.data.length >= 0) {
          response.data = response.data.map((item: any) => toCamelCase(item));
        }
      } else {
        response.data = toCamelCase(response.data);
      }
    }
    return response;
  },
  (error) => {
    console.error('🔴 AI/Extended API Error:', {
      message: error.message,
      code: error.code,
      url: error.config?.url,
      timeout: error.config?.timeout,
    });
    // Handle 401 same as main client
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath.includes('/login') || currentPath.includes('/register');
      if (!isAuthPage) {
        localStorage.removeItem('token');
        redirectToLoginOnTokenExpired();
      }
    }
    return Promise.reject(error);
  }
);

export { apiClient };
export default apiClient;
